/**
 * useSpreadsheetConnectState — orchestration hook for
 * ``SpreadsheetConnectDialog``. Owns all dialog state + side-effect
 * plumbing (preview mutation, file/cloud selection, sheet picking,
 * submit) so the dialog file can stay focused on the Radix Dialog
 * chrome + per-step delegation. NOT a public API — consumed only by
 * ``SpreadsheetConnectDialog``.
 */

import { useLingui } from "@lingui/react/macro";
import * as React from "react";

import type { ConnectionSummary } from "@ando/core";

import type {
  ImportSpreadsheetOptions,
  SpreadsheetPreviewResponse,
} from "../../api/spreadsheetApi";
import {
  type ChunkedUploadProgress,
  useChunkedSpreadsheetUpload,
} from "../../hooks/useChunkedSpreadsheetUpload";
import { usePreviewSpreadsheet } from "../../hooks/usePreviewSpreadsheet";
import type { SpreadsheetSourceMode } from "../../wizards/spreadsheetSpec";

/**
 * Soglia per il path chunked: oltre questo size il file viene
 * spezzato + uploadato via S3 multipart per evitare il cap GFE di
 * 32 MB. Sotto soglia, ``importSpreadsheet`` accetta direttamente il
 * ``file`` multipart classico (1 round-trip vs 3, throughput
 * praticamente equivalente per file piccoli).
 */
const CHUNKED_UPLOAD_THRESHOLD_BYTES = 25 * 1024 * 1024;
import {
  type CloudFileSelection,
  type Phase,
  type PickerOpener,
  type PreviewInput,
  SOURCE_PROVIDER_LABEL,
  fileBaseName,
  humanizeFileError,
} from "./types";

export interface UseSpreadsheetConnectStateArgs {
  readonly open: boolean;
  readonly sourceMode: SpreadsheetSourceMode;
  readonly cloudConnections: readonly ConnectionSummary[];
  readonly openPicker: PickerOpener | undefined;
  readonly onSubmit: (
    options: ImportSpreadsheetOptions,
  ) => Promise<{ readonly connector_id: string }>;
  readonly onSuccess: (connectorId: string) => void;
}

export interface UseSpreadsheetConnectStateResult {
  readonly phase: Phase;
  readonly submitting: boolean;
  readonly preview: SpreadsheetPreviewResponse | null;
  readonly previewLoading: boolean;
  readonly onStep2: boolean;
  readonly error: string | null;
  /**
   * Progress dell'upload chunked — ``null`` se l'upload non è in corso
   * (o se siamo nel path multipart classico per file < 25 MB). Il
   * dialog renderizza una progress bar quando questo non è null +
   * ``submitting === true``.
   */
  readonly uploadProgress: ChunkedUploadProgress | null;
  /** Cancel callback — interrompe l'upload chunked in volo + abort lato MinIO. */
  readonly cancelUpload: () => void;
  // Step 1 — upload
  readonly file: File | null;
  readonly fileInputRef: React.RefObject<HTMLInputElement | null>;
  readonly dragOver: boolean;
  readonly onDragOver: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDragLeave: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDrop: (e: React.DragEvent<HTMLElement>) => void;
  readonly onDropzoneClick: () => void;
  readonly onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  // Step 1 — cloud
  readonly cloudFile: CloudFileSelection | null;
  readonly selectedConnectionKey: string | null;
  readonly setSelectedConnectionKey: (key: string | null) => void;
  readonly handleOpenCloudPicker: () => void;
  // Step 2
  readonly connectorName: string;
  readonly setConnectorName: (name: string) => void;
  readonly selectedSheets: readonly string[];
  readonly allSheetsSelected: boolean;
  readonly toggleSheet: (name: string) => void;
  readonly toggleAllSheets: () => void;
  readonly handleBack: () => void;
  readonly handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

export function useSpreadsheetConnectState(
  args: UseSpreadsheetConnectStateArgs,
): UseSpreadsheetConnectStateResult {
  const { open, sourceMode, cloudConnections, openPicker, onSubmit, onSuccess } = args;
  const { t } = useLingui();

  const [phase, setPhase] = React.useState<Phase>("idle");
  const [error, setError] = React.useState<string | null>(null);

  const previewMutation = usePreviewSpreadsheet();
  const preview: SpreadsheetPreviewResponse | null = previewMutation.data ?? null;
  const previewLoading = previewMutation.isPending;
  const previewError = previewMutation.error?.message ?? null;

  // Chunked upload driver — ``.upload(file)`` può girare due volte sul
  // turno (oversize preview + oversize import), ma il pattern reale è
  // un singolo upload memoizzato in ``presignedKey`` e riusato sia per
  // ``preview-spreadsheet`` che per ``import-spreadsheet``. Zero
  // re-upload del file dopo il primo round-trip a MinIO.
  const chunkedUpload = useChunkedSpreadsheetUpload();
  const [presignedKey, setPresignedKey] = React.useState<string | null>(null);

  const onPreview = React.useCallback(
    async (input: PreviewInput): Promise<void> => {
      if (input.kind === "upload") {
        // Oversize? → presigned MinIO PUT, poi preview con ``source_presigned_key``.
        // Bypassa il cap GFE di ~32 MB sul body multipart (Jun 14, 2026 fix).
        if (input.file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES) {
          // Chunked upload errors don't ride ``previewMutation.error`` —
          // they're surfaced explicitly here so the user sees "Upload
          // interrotto / connessione persa" instead of a frozen
          // "Analizzo il foglio…" spinner. The preview mutation
          // ``try/catch`` below stays untouched because TanStack
          // surfaces those via ``previewMutation.error`` natively.
          let result: Awaited<ReturnType<typeof chunkedUpload.upload>>;
          try {
            result = await chunkedUpload.upload(input.file);
          } catch (uploadErr) {
            const message =
              uploadErr instanceof Error && uploadErr.message
                ? uploadErr.message
                : t`Caricamento del file non riuscito. Riprova.`;
            setError(message);
            setPresignedKey(null);
            return;
          }
          setPresignedKey(result.object_key);
          try {
            await previewMutation.mutateAsync({
              source_presigned_key: result.object_key,
            });
          } catch {
            // The presigned key is for a file the BE couldn't parse —
            // clear it so the user can't resubmit a stale key on retry.
            // The actual error message is shown via ``previewMutation.error``.
            setPresignedKey(null);
          }
          return;
        }
        setPresignedKey(null);
        try {
          await previewMutation.mutateAsync({ file: input.file });
        } catch {
          // Surfaced via ``previewMutation.error``.
        }
        return;
      }
      setPresignedKey(null);
      try {
        await previewMutation.mutateAsync({
          source_file_id: input.source_file_id,
          source_provider: input.source_provider,
          ...(input.source_connection_key !== undefined
            ? { source_connection_key: input.source_connection_key }
            : {}),
        });
      } catch {
        // Surfaced via ``previewMutation.error``.
      }
    },
    [chunkedUpload, previewMutation, t],
  );

  const onResetPreview = React.useCallback(() => {
    previewMutation.reset();
  }, [previewMutation]);

  // Step 1 state.
  const [file, setFile] = React.useState<File | null>(null);
  const [cloudFile, setCloudFile] = React.useState<CloudFileSelection | null>(null);
  const [selectedConnectionKey, setSelectedConnectionKey] = React.useState<string | null>(null);

  // Step 2 state.
  const [connectorName, setConnectorName] = React.useState("");
  const [selectedSheets, setSelectedSheets] = React.useState<readonly string[]>([]);

  // Guards.
  const importingRef = React.useRef(false);
  const pickerOpeningRef = React.useRef(false);

  // Reset on (re-)open.
  React.useEffect(() => {
    if (!open) return;
    setPhase("idle");
    setError(null);
    setFile(null);
    setCloudFile(null);
    setConnectorName("");
    setSelectedSheets([]);
    setPresignedKey(null);
    importingRef.current = false;
    pickerOpeningRef.current = false;
    onResetPreview();
    if (sourceMode !== "upload" && cloudConnections.length === 1) {
      setSelectedConnectionKey(cloudConnections[0]?.connection_key ?? null);
    } else {
      setSelectedConnectionKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceMode]);

  // Seed Step 2 defaults whenever a fresh preview lands.
  React.useEffect(() => {
    if (!preview) return;
    setSelectedSheets(preview.sheets.map((s) => s.original_name));
    if (connectorName.trim().length === 0) {
      const baseName = file?.name ?? cloudFile?.name ?? t`Foglio`;
      setConnectorName(fileBaseName(baseName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview]);

  const cloudProvider = sourceMode as Exclude<SpreadsheetSourceMode, "upload">;
  const submitting = phase === "submitting";
  const onStep2 = preview !== null;

  // ── Upload handler ────────────────────────────────────────────────
  const handleFileSelected = React.useCallback(
    (picked: File | null) => {
      setError(null);
      // Una selezione fresca invalida sempre il presigned_key precedente:
      // il blob su MinIO si riferiva al vecchio file. ``onPreview`` ne
      // produrrà uno nuovo se ``picked.size`` è oversize.
      setPresignedKey(null);
      if (!picked) {
        setFile(null);
        onResetPreview();
        return;
      }
      const fileErr = humanizeFileError(picked);
      if (fileErr !== null) {
        setError(fileErr);
        setFile(null);
        return;
      }
      setFile(picked);
      void onPreview({ kind: "upload", file: picked });
    },
    [onPreview, onResetPreview],
  );

  // ── Cloud picker handler ──────────────────────────────────────────
  const handleOpenCloudPicker = React.useCallback(() => {
    void (async () => {
      if (pickerOpeningRef.current || openPicker === undefined) return;
      if (selectedConnectionKey === null && cloudConnections.length > 1) {
        setError(t`Seleziona prima un account ${SOURCE_PROVIDER_LABEL[cloudProvider]}.`);
        return;
      }
      pickerOpeningRef.current = true;
      setError(null);
      try {
        const result = await openPicker({
          provider: cloudProvider,
          connectionKey: selectedConnectionKey ?? cloudConnections[0]?.connection_key ?? undefined,
          spreadsheetOnly: true,
          allowFiles: true,
          allowFolders: false,
        });
        if (result.cancelled || result.items.length === 0) return;
        const item = result.items[0];
        if (!item || item.is_folder) {
          setError(
            t`Il picker ha restituito una cartella. Seleziona un file foglio (.xlsx/.xls/.csv).`,
          );
          return;
        }
        const sel: CloudFileSelection = {
          id: item.id,
          name: item.name,
          mime: item.mime_type,
          size_bytes: item.size_bytes,
          provider: cloudProvider,
          connectionKey: selectedConnectionKey ?? cloudConnections[0]?.connection_key ?? undefined,
        };
        setCloudFile(sel);
        void onPreview({
          kind: "cloud",
          source_file_id: sel.id,
          source_provider: cloudProvider,
          source_connection_key: sel.connectionKey,
        });
      } finally {
        pickerOpeningRef.current = false;
      }
    })();
  }, [cloudConnections, cloudProvider, onPreview, openPicker, selectedConnectionKey, t]);

  // ── Drag&drop ─────────────────────────────────────────────────────
  const [dragOver, setDragOver] = React.useState(false);
  const onDragOver = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);
  const onDragLeave = React.useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);
  const onDrop = React.useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const picked = e.dataTransfer.files?.[0] ?? null;
      handleFileSelected(picked);
    },
    [handleFileSelected],
  );

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const onDropzoneClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const onFileChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelected(e.target.files?.[0] ?? null);
    },
    [handleFileSelected],
  );

  // ── Step 2 — Sheet toggling ───────────────────────────────────────
  const toggleSheet = React.useCallback((sheetName: string) => {
    setSelectedSheets((prev) =>
      prev.includes(sheetName) ? prev.filter((s) => s !== sheetName) : [...prev, sheetName],
    );
  }, []);

  const allSheetsSelected = React.useMemo(() => {
    if (!preview) return false;
    return selectedSheets.length === preview.sheets.length;
  }, [preview, selectedSheets]);

  const toggleAllSheets = React.useCallback(() => {
    if (!preview) return;
    if (allSheetsSelected) setSelectedSheets([]);
    else setSelectedSheets(preview.sheets.map((s) => s.original_name));
  }, [allSheetsSelected, preview]);

  // ── Back ──────────────────────────────────────────────────────────
  const handleBack = React.useCallback(() => {
    setError(null);
    setSelectedSheets([]);
    setConnectorName("");
    setFile(null);
    setCloudFile(null);
    onResetPreview();
  }, [onResetPreview]);

  // ── Submit ────────────────────────────────────────────────────────
  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      void (async () => {
        if (importingRef.current) return;
        setError(null);

        const trimmedName = connectorName.trim();
        if (trimmedName.length === 0) {
          setError(t`Inserisci un nome per il connettore.`);
          return;
        }
        if (selectedSheets.length === 0) {
          setError(t`Seleziona almeno un foglio da importare.`);
          return;
        }

        importingRef.current = true;
        setPhase("submitting");
        try {
          // ── Resolve baseOptions ── il branch ``upload`` con file
          // oversize (> 25 MB) attiva il flusso chunked: il file
          // viaggia in N PUT da 16 MB direttamente al BE-proxy →
          // MinIO multipart, e l'``object_key`` finale viene messo
          // su ``source_presigned_key``. Il BE riceve così SOLO il
          // riferimento a MinIO sull'``import-spreadsheet`` finale,
          // bypassando il cap GFE di 32 MB.
          //
          // I file piccoli (≤ 25 MB) restano sul path classico
          // (multipart inline con il body) per non pagare 3 round-trip
          // (start/part/complete) quando 1 basta. Soglia ben sotto
          // i 32 MB GFE per avere margine sui form overhead.
          let baseOptions: ImportSpreadsheetOptions;
          if (sourceMode === "upload") {
            if (!file) {
              baseOptions = { connector_name: trimmedName };
            } else if (file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES) {
              // Jun 14, 2026 — riusa il presigned_key prodotto da
              // ``onPreview`` se già disponibile: il file è già su
              // MinIO, evitiamo un secondo upload di N×16 MB part.
              // Fallback: se per qualche motivo manca (es. utente ha
              // raggiunto Step 2 senza preview oversize), uploadiamo
              // qui.
              const objectKey =
                presignedKey ?? (await chunkedUpload.upload(file)).object_key;
              baseOptions = {
                connector_name: trimmedName,
                source_presigned_key: objectKey,
              };
            } else {
              baseOptions = { connector_name: trimmedName, file };
            }
          } else if (!cloudFile) {
            baseOptions = { connector_name: trimmedName };
          } else {
            baseOptions = {
              connector_name: trimmedName,
              source_file_id: cloudFile.id,
              source_provider: cloudProvider,
              ...(cloudFile.connectionKey !== undefined
                ? { source_connection_key: cloudFile.connectionKey }
                : {}),
            };
          }

          const options: ImportSpreadsheetOptions = {
            ...baseOptions,
            sheet_names: selectedSheets,
          };

          const result = await onSubmit(options);
          onSuccess(result.connector_id);
        } catch (err) {
          const message = err instanceof Error ? err.message : t`Import non riuscito.`;
          setError(message);
          setPhase("idle");
        } finally {
          importingRef.current = false;
        }
      })();
    },
    [
      chunkedUpload.upload,
      cloudFile,
      cloudProvider,
      connectorName,
      file,
      onSubmit,
      onSuccess,
      presignedKey,
      selectedSheets,
      sourceMode,
      t,
    ],
  );

  // ── Surface preview error ─────────────────────────────────────────
  React.useEffect(() => {
    if (previewError) setError(previewError);
  }, [previewError]);

  return {
    phase,
    submitting,
    preview,
    previewLoading,
    onStep2,
    error,
    uploadProgress: chunkedUpload.progress,
    cancelUpload: chunkedUpload.cancel,
    file,
    fileInputRef,
    dragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    onDropzoneClick,
    onFileChange,
    cloudFile,
    selectedConnectionKey,
    setSelectedConnectionKey,
    handleOpenCloudPicker,
    connectorName,
    setConnectorName,
    selectedSheets,
    allSheetsSelected,
    toggleSheet,
    toggleAllSheets,
    handleBack,
    handleSubmit,
  };
}
